/**
 * Default i18n bond setup.
 *
 * Composes @molecule/app-i18n-default-react with this app's English UI
 * translations, a per-locale lazy loader, and the companion locale
 * bonds for every shared @molecule/app-* dependency. The dynamic-import
 * template literal MUST stay in the app so Vite can code-split each
 * locale's `ui.ts` into its own chunk.
 *
 * The `packageLocales` array is generated by
 * `scripts/wire-app-locale-bonds.mjs` from this app's package.json
 * dependencies. Re-run that script after adding/removing any
 * `@molecule/app-*` dep to refresh this list.
 */

import { setupI18nDefault as setupI18nProvider } from '@molecule/app-i18n-default-react'
import * as aiChatLocales from '@molecule/app-locales-ai-chat'
import * as authLocales from '@molecule/app-locales-auth'
import * as badgeLocales from '@molecule/app-locales-badge'
import * as chartsLocales from '@molecule/app-locales-charts'
import * as codeEditorLocales from '@molecule/app-locales-code-editor'
import * as footerLocales from '@molecule/app-locales-footer'
import * as formsLocales from '@molecule/app-locales-forms'
import * as httpLocales from '@molecule/app-locales-http'
import * as i18nLocales from '@molecule/app-locales-i18n'
import * as iconsLocales from '@molecule/app-locales-icons'
import * as ideLocales from '@molecule/app-locales-ide'
import * as livePreviewLocales from '@molecule/app-locales-live-preview'
import * as oauthButtonsLocales from '@molecule/app-locales-oauth-buttons'
import * as pushLocales from '@molecule/app-locales-push'
import * as reactLocales from '@molecule/app-locales-react'
import * as rootErrorBoundaryLocales from '@molecule/app-locales-root-error-boundary'
import * as routingLocales from '@molecule/app-locales-routing'
import * as settingsPanelLocales from '@molecule/app-locales-settings-panel'
import * as storageLocales from '@molecule/app-locales-storage'
import * as uiLocales from '@molecule/app-locales-ui'
import * as utilitiesLocales from '@molecule/app-locales-utilities'
import * as versionLocales from '@molecule/app-locales-version'

import { ui as en } from '../locales/en/ui.js'

const lazyLoadUi = (code: string): Promise<Record<string, string>> =>
  import(`../locales/${code}/ui.ts`).then((m: { ui: Record<string, string> }) => m.ui)

export const i18nProvider = setupI18nProvider({
  enUi: en,
  lazyLoadUi,
  // English-only: a public status page is operational infrastructure, and
  // every incident word on it must match the words in the runbook, the RSS
  // feed, and the API's health vocabulary. Browser-locale auto-detection is
  // therefore disabled — 'en' is the only registered locale.
  supportedLocales: ['en'],
  packageLocales: [
    aiChatLocales,
    authLocales,
    badgeLocales,
    chartsLocales,
    codeEditorLocales,
    footerLocales,
    formsLocales,
    httpLocales,
    i18nLocales,
    iconsLocales,
    ideLocales,
    livePreviewLocales,
    oauthButtonsLocales,
    pushLocales,
    reactLocales,
    rootErrorBoundaryLocales,
    routingLocales,
    settingsPanelLocales,
    storageLocales,
    uiLocales,
    utilitiesLocales,
    versionLocales,
  ],
})

/**
 * Called from bonds/index.ts during setupProviders(). The i18n
 * provider was already wired at module-eval time above — this
 * function exists for compat with the legacy bond-setup contract
 * and is a no-op.
 */
export function setupI18nDefault(): void {
  // intentionally empty
}
